import * as S from './styles';
import { ToolTip } from '@/components';
import { formatCurrency, getOnStorage } from '@/shared';
import { cleanInput } from '@/shared/commonFunctions';
import { useCallback, useMemo, useState, useEffect, useRef } from 'react';
import { useCart, useDiscountAmountFlex, useDiscountFlex, useUserContext, useAlert, useTables } from '@/hooks';
import { DiscountStorageProps, PackagesTypesProps, PackagingProductProps, ProductProps } from '@/types';

const addIcon = `${process.env.PUBLIC_URL}/addIcon.svg`;
const trashIcon = `${process.env.PUBLIC_URL}/trashIcon.svg`;
const removeIcon = `${process.env.PUBLIC_URL}/removeIcon.svg`;

interface ComboSectionCounter {
  /** Quantidade alocada nesta seção/instância (o que o contador exibe). */
  displayQuantity: number;
  /** Decremento direcionado à instância desta seção (preserva outros kits). */
  onDecrement?: () => void;
  /** Incremento direcionado à instância desta seção (preserva outros kits). */
  onIncrement?: () => void;
  /** Ajuste direcionado com delta arbitrário (ex.: digitação no contador). */
  onAdjust?: (delta: number) => void;
  /** Remove apenas a parcela desta seção/instância (preserva outros kits). */
  onRemove?: () => void;
}

interface CounterProps {
  id: string;
  limitFlex: number;
  showBorder?: boolean;
  product: ProductProps;
  pack: PackagingProductProps;
  discountProduct?: DiscountStorageProps;
  background: 'light' | 'white';
  /** Modo legado: parcela combo vs excedente na mesma linha. */
  portionQuantity?: number;
  siblingQuantity?: number;
  /** Modo combo no carrinho: exibe alocação da seção, altera a linha do carrinho em ±1. */
  comboSectionCounter?: ComboSectionCounter;
  cartItemUid?: string;
}

const Counter = ({
  id,
  pack,
  product,
  limitFlex,
  showBorder,
  background,
  discountProduct,
  portionQuantity,
  siblingQuantity = 0,
  comboSectionCounter,
  cartItemUid,
}: CounterProps) => {
  const generatedUid = useMemo(() => {
    if (pack?.QTD_MULTIPLO_VENDA > 1) {
      return `${product?.COD_PRODUTO}-${pack?.SG_EMBALAGEM}-${pack?.QTD_MULTIPLO_VENDA}`;
    }
    return `${product?.COD_PRODUTO}-${pack?.SG_EMBALAGEM}-${pack?.QTD_EMBALAGEM}`;
  }, [product, pack]);

  const uid = cartItemUid ?? generatedUid;
  const isComboSectionMode = comboSectionCounter != null;
  const isPortionMode = !isComboSectionMode && portionQuantity != null;

  const { showAlert } = useAlert();
  const { tables } = useTables();
  const { rcaSaldoFlex } = useUserContext();
  const { deleteDiscount } = useDiscountFlex();
  const debounceTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [isOpenTooltip, setOpenTooltip] = useState(false);
  const { updateProductAmount, removeProductFromCart, checkLimit, currentCartDetails } = useCart();
  const { discountAmountAll: discountAmount } = useDiscountAmountFlex();

  const quantity = useMemo(() => {
    if (cartItemUid && currentCartDetails?.items?.length) {
      const byUid = currentCartDetails.items.find((i) => i.uid === cartItemUid);
      if (byUid) return byUid.quantity;
    }

    if (currentCartDetails?.items?.length > 0) {
      return (
        currentCartDetails?.items?.find((i) => {
          const [emb, qtd] = i?.option_uid?.split('_') as [PackagesTypesProps, string];

          if (pack?.QTD_MULTIPLO_VENDA > 1 && emb === 'PK') {
            return i?.sku === product?.COD_PRODUTO && pack?.SG_EMBALAGEM === emb && qtd === pack?.QTD_MULTIPLO_VENDA?.toString();
          }

          return i?.sku === product?.COD_PRODUTO && pack?.SG_EMBALAGEM === emb && qtd === pack?.QTD_EMBALAGEM?.toString();
        })?.quantity || 0
      );
    } else {
      return 0;
    }
  }, [currentCartDetails, cartItemUid, product?.COD_PRODUTO, pack]);

  const cartLineQuantity = quantity;

  const visibleQuantity = isComboSectionMode
    ? comboSectionCounter.displayQuantity
    : isPortionMode
      ? portionQuantity
      : quantity;

  const [counter, setCounter] = useState(visibleQuantity);
  const [currentCounter, setCurrentCounter] = useState(`${visibleQuantity}`);

  useEffect(() => {
    setCounter(visibleQuantity);
    setCurrentCounter(`${visibleQuantity}`);
  }, [visibleQuantity, id]);

  const getNextTotalQuantity = useCallback(
    (nextPortionQuantity: number) => nextPortionQuantity + (isPortionMode ? siblingQuantity : 0),
    [isPortionMode, siblingQuantity]
  );

  const updateCartLineQuantity = useCallback(
    (nextLineQuantity: number) => {
      updateProductAmount({
        uid,
        sku: product?.COD_PRODUTO,
        quantity: Math.max(0, nextLineQuantity),
      });
    },
    [uid, product?.COD_PRODUTO, updateProductAmount]
  );

  const handleDecrement = useCallback(
    (qtd: number) => {
      setOpenTooltip(false);

      if (isComboSectionMode) {
        if (comboSectionCounter.displayQuantity <= 1) {
          setCounter(0);
          if (comboSectionCounter.onRemove) {
            comboSectionCounter.onRemove();
          } else {
            removeProductFromCart({ uid });
          }
          return;
        }
        if (comboSectionCounter.onDecrement) {
          comboSectionCounter.onDecrement();
          return;
        }
        updateCartLineQuantity(cartLineQuantity - 1);
        return;
      }

      if (isPortionMode && qtd <= 1 && siblingQuantity > 0) {
        updateProductAmount({
          uid,
          sku: product?.COD_PRODUTO,
          quantity: siblingQuantity,
        });
        setCounter(0);
        return;
      }

      if (qtd > 0) {
        const nextTotal = getNextTotalQuantity(qtd - 1);
        updateProductAmount({
          uid,
          sku: product?.COD_PRODUTO,
          quantity: nextTotal,
        });
        setCounter(qtd - 1);
      } else {
        setCounter(0);
        removeProductFromCart({ uid });
      }
    },
    [
      uid,
      product?.COD_PRODUTO,
      updateProductAmount,
      removeProductFromCart,
      isComboSectionMode,
      isPortionMode,
      siblingQuantity,
      getNextTotalQuantity,
      cartLineQuantity,
      updateCartLineQuantity,
      comboSectionCounter,
    ]
  );

  const handleRemoveOnCart = useCallback(() => {
    if (isComboSectionMode && comboSectionCounter?.onRemove) {
      setCounter(0);
      comboSectionCounter.onRemove();
      return;
    }

    let packOption = pack?.SG_EMBALAGEM;
    let packQtd = pack?.QTD_EMBALAGEM;
    if (pack?.QTD_MULTIPLO_VENDA > 1) {
      packQtd = pack?.QTD_MULTIPLO_VENDA;
    }
    deleteDiscount({
      package: packOption,
      quantity: packQtd,
      productId: product.COD_PRODUTO,
      uid,
    });
    setCounter(0);
    removeProductFromCart({ uid });
  }, [uid, pack, product?.COD_PRODUTO, deleteDiscount, removeProductFromCart, isComboSectionMode, comboSectionCounter]);

  const handleIncrement = useCallback(
    (currentPortionQuantity: number) => {
      setOpenTooltip(false);

      if (isComboSectionMode) {
        if (comboSectionCounter.onIncrement) {
          comboSectionCounter.onIncrement();
          return;
        }
        updateCartLineQuantity(cartLineQuantity + 1);
        return;
      }

      const nextTotal = getNextTotalQuantity(currentPortionQuantity + 1);
      updateProductAmount({
        uid,
        quantity: nextTotal,
        sku: product?.COD_PRODUTO,
      });
      setCounter(currentPortionQuantity + 1);
    },
    [uid, product?.COD_PRODUTO, updateProductAmount, getNextTotalQuantity, isComboSectionMode, cartLineQuantity, updateCartLineQuantity]
  );

  const calcLimit = useCallback(
    (qtd: number) => {
      return checkLimit(uid, product?.COD_PRODUTO, qtd, currentCartDetails?.items || [], false);
    },
    [uid, product.COD_PRODUTO, currentCartDetails, checkLimit]
  );

  const limit = useMemo(() => {
    const nextTotal = isComboSectionMode
      ? cartLineQuantity + 1
      : isPortionMode
        ? counter + siblingQuantity + 1
        : counter + 1;
    return calcLimit(nextTotal);
  }, [calcLimit, counter, isComboSectionMode, cartLineQuantity, isPortionMode, siblingQuantity]);

  const handleTooltip = useCallback(() => {
    setOpenTooltip(true);
    setTimeout(() => setOpenTooltip(false), 5000);
  }, [setOpenTooltip]);

  const isLimit = useMemo(() => {
    return counter >= limit;
  }, [counter, limit]);

  const tooltipMessage = useMemo(() => {
    if (!isLimit) {
      return `Saldo Flex Indiponível: ${formatCurrency({
        value: (rcaSaldoFlex || 0) + discountAmount,
      })}`;
    } else {
      return `Produto com limite de estoque atingido`;
    }
  }, [isLimit, rcaSaldoFlex, discountAmount]);

  const isLimitFlex = useMemo(() => {
    return !!discountProduct?.percentual && discountProduct?.percentual < 0 && counter >= limitFlex;
  }, [counter, limitFlex, discountProduct]);

  // Para flex positivo não devemos bloquear decremento via limitFlex
  const isLimitFlexDown = useMemo(() => false, []);

  const isLimitUp = useMemo(() => {
    return counter >= limit || isLimitFlex;
  }, [limit, counter, isLimitFlex]);

  useEffect(() => {
    if (!isOpenTooltip || isLimit) return;

    const discounts = getOnStorage('discounts') || {};
    const clients = Object.keys(discounts)
      .map((client) => {
        const clientName = tables.current?.cliente?.find((c) => c.COD_CLIENTE === Number(client))?.NM_CLIENTE || client;

        return `<p style="font-size: 12px; ellipsis: true; overflow: hidden; text-overflow: ellipsis; white-space: nowrap;"><b>${client}</b>: ${clientName}</p>`;
      })
      .join('');

    showAlert({
      bgType: 'primary',
      bordered: true,
      theme: 'warning',
      close: 'icon',
      message: clients,
      title: `Saldo Flex Indiponível, você tem flex aplicado nos seguintes clientes:`,
      isList: false,
    });
  }, [isOpenTooltip, isLimit]);

  const handleBlur = useCallback(
    (count: number) => {
      if (isComboSectionMode) {
        if (!count) {
          handleRemoveOnCart();
          return;
        }

        const delta = count - comboSectionCounter.displayQuantity;
        const nextLineQuantity = cartLineQuantity + delta;
        const curLimit = calcLimit(nextLineQuantity);
        const discount = discountProduct?.percentual;
        const isStockLimit = !discount && nextLineQuantity > curLimit;
        const isFlexLimit = discount && discount < 0 ? nextLineQuantity > limitFlex : false;

        if (isStockLimit || isFlexLimit) {
          const hierarchy = isFlexLimit ? (curLimit < limitFlex ? curLimit : limitFlex || curLimit) : curLimit;
          const cappedDelta = hierarchy - cartLineQuantity;
          if (cappedDelta !== 0 && comboSectionCounter.onAdjust) {
            comboSectionCounter.onAdjust(cappedDelta);
            return;
          }
          updateCartLineQuantity(hierarchy);
          return;
        }

        if (delta !== 0 && comboSectionCounter.onAdjust) {
          comboSectionCounter.onAdjust(delta);
          return;
        }

        updateCartLineQuantity(nextLineQuantity);
        return;
      }

      if (!count) {
        if (isPortionMode && siblingQuantity > 0) {
          updateProductAmount({ uid, quantity: siblingQuantity, sku: product?.COD_PRODUTO });
          setCounter(0);
          setCurrentCounter('0');
          return;
        }

        handleRemoveOnCart();
        return;
      }

      const nextTotal = getNextTotalQuantity(count);
      const curLimit = calcLimit(nextTotal);
      const discount = discountProduct?.percentual;
      const isStockLimit = !discount && nextTotal > curLimit;
      const isFlexLimit = discount && discount < 0 ? nextTotal > limitFlex : false;
      const isCurrentLimit = isStockLimit || isFlexLimit;

      if (isCurrentLimit) {
        const hierarchy = isFlexLimit ? (curLimit < limitFlex ? curLimit : limitFlex || curLimit) : curLimit;
        const allowedPortion = isPortionMode ? Math.max(hierarchy - siblingQuantity, 0) : hierarchy;
        setCounter(allowedPortion);
        setCurrentCounter(`${allowedPortion}`);
        updateProductAmount({ uid, quantity: hierarchy, sku: product?.COD_PRODUTO });
        return;
      }

      updateProductAmount({ uid, quantity: nextTotal, sku: product?.COD_PRODUTO });
      setCounter(count);
    },
    [
      uid,
      product?.COD_PRODUTO,
      limitFlex,
      discountProduct?.percentual,
      updateProductAmount,
      calcLimit,
      handleRemoveOnCart,
      isPortionMode,
      isComboSectionMode,
      comboSectionCounter,
      siblingQuantity,
      getNextTotalQuantity,
      cartLineQuantity,
      updateCartLineQuantity,
    ]
  );

  const changeValue = useCallback(
    (qtd: string) => {
      const cleanValue = cleanInput(qtd);
      setCurrentCounter(cleanValue);

      if (!cleanValue) return;

      const qtdParsed = parseInt(cleanValue);
      if (debounceTimeoutRef.current) clearTimeout(debounceTimeoutRef.current);
      debounceTimeoutRef.current = setTimeout(() => {
        debounceTimeoutRef.current = null;
        if (qtdParsed) handleBlur(qtdParsed);
      }, 300);
    },
    [handleBlur]
  );

  useEffect(() => {
    return () => {
      if (debounceTimeoutRef.current) clearTimeout(debounceTimeoutRef.current);
    };
  }, []);

  return (
    <ToolTip direction="top" show={isOpenTooltip} displayMode="default" text={tooltipMessage}>
      <S.CounterWrapper $background={background}>
        <S.CounterButton
          data-test={`decrement-counter-component-${id}`}
          $islimit={isLimitFlexDown}
          onClick={() =>
            isLimitFlexDown
              ? handleTooltip()
              : isComboSectionMode
                ? comboSectionCounter.displayQuantity <= 1
                  ? handleRemoveOnCart()
                  : handleDecrement(counter)
                : counter <= 1 && (!isPortionMode || siblingQuantity === 0)
                  ? handleRemoveOnCart()
                  : handleDecrement(counter)
          }
        >
          <S.Icon src={counter === 1 ? trashIcon : removeIcon} />
        </S.CounterButton>

        <S.Input
          type="text"
          value={currentCounter}
          $showborder={showBorder}
          onBlur={() => {
            // Cancela o debounce da digitação: sem isso, um blur disparado por clique nos botões
            // +/- (mousedown tira o foco antes do click) processa o valor digitado aqui, e o timer
            // pendente dispara de novo ~300ms depois com dados obsoletos, aplicando o ajuste em duplicidade.
            if (debounceTimeoutRef.current) {
              clearTimeout(debounceTimeoutRef.current);
              debounceTimeoutRef.current = null;
            }
            handleBlur(Number(currentCounter));
          }}
          onChange={(e) => changeValue(e.target.value?.replace(/[^0-9.]/g, ''))}
        />

        <S.CounterButton
          $islimit={isLimitUp}
          data-test={`increment-counter-component-${id}`}
          onClick={() => (isLimitFlex || isLimitUp ? handleTooltip() : handleIncrement(counter))}
        >
          <S.Icon src={addIcon} />
        </S.CounterButton>
      </S.CounterWrapper>
    </ToolTip>
  );
};

export default Counter;
